import type { ClientError } from '@tribeplatform/gql-client/lib'
import type {
  MutationUpdateNetworkMediaSettingsArgs,
  Network,
  MediaNetworkSettings,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import { useCachedNetwork } from '../cache/useCachedNetwork.js'

export const useUpdateNetworkMediaSettings = (options?: {
  useMutationOptions?: UseMutationOptions<
    MediaNetworkSettings,
    ClientError,
    MutationUpdateNetworkMediaSettingsArgs,
    { snapshot: Network }
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const networkSnapshot = useCachedNetwork()

  return useMutation<
    MediaNetworkSettings,
    ClientError,
    MutationUpdateNetworkMediaSettingsArgs,
    { snapshot: Network }
  >(variables => client.media.updateNetworkSettings(variables), {
    onMutate: async newNetworkMediaSettings => {
      await queryClient.cancelQueries(networkKey)

      const snapshot = networkSnapshot

      queryClient.setQueriesData<Network>(networkKey, old => {
        return {
          ...old,
          settings: {
            ...old?.settings,
            fileSizeLimit:
              newNetworkMediaSettings?.input?.fileSizeLimit ??
              old?.settings?.fileSizeLimit,
            imageSizeLimit:
              newNetworkMediaSettings?.input?.imageSizeLimit ??
              old?.settings?.imageSizeLimit,
            fileTypesLimit:
              newNetworkMediaSettings?.input?.fileTypesLimit ??
              old?.settings?.fileTypesLimit,
            imageTypesLimit:
              newNetworkMediaSettings?.input?.imageTypesLimit ??
              old?.settings?.imageTypesLimit,
          },
        }
      })

      return { snapshot }
    },
    onError: (err, newNetworkSettings, context) => {
      // revert back the optimistic response
      queryClient.setQueriesData<Network>(networkKey, context.snapshot)
    },
    onSettled: () => {
      // invalidate to refetch again
      queryClient.invalidateQueries(networkKey)
    },
    ...useMutationOptions,
  })
}
